import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router } from '@angular/router';
import { findRouteData } from 'gain-web/lib/router/find-route-data';
import { getRouteParamFromRoot } from 'gain-web/lib/router/find-route-param';
import { ApiClient } from 'gain-web/shared-services/api-client.generated.service';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import CalculationContextDto = ApiClient.CalculationContextDto;

@Injectable()
export class CalculationContextService {
  public get value(): CalculationContext {
    return getCalculationContext(this._router.routerState.root.snapshot);
  }

  public get valueChanges(): Observable<CalculationContext> {
    return this._router.events.pipe(map(() => this.value));
  }

  constructor(private _router: Router) {}
}

export function getCalculationContext(
  route: ActivatedRouteSnapshot,
): CalculationContext {
  const calculationContext = findCalculationContext(route);
  if (calculationContext == null) {
    throw new Error('Unable to find calculation context in route tree');
  }
  return calculationContext;
}

export function findCalculationContext(
  route: ActivatedRouteSnapshot,
): CalculationContext | undefined {
  return findRouteData<CalculationContext>(route, 'calculationContext');
}

export async function findOrFetchCalculationContext(
  route: ActivatedRouteSnapshot,
  calculationContextService: ApiClient.CalculationContextService,
): Promise<CalculationContext> {
  const calculationContext = findCalculationContext(route);
  if (calculationContext == null) {
    const portalId = getRouteParamFromRoot(route, 'portalId');
    const calculationId = getRouteParamFromRoot(route, 'calculationId');
    const ccr = await calculationContextService
      .getCalculationContextById(portalId, calculationId)
      .toPromise();
    return new CalculationContext(
      new CalculationContextDto(ccr.result),
      calculationContextService,
    );
  }
  return calculationContext;
}

export function findTransactionInputByGuid(
  inputs: ApiClient.CalculationEngineInputs,
  transactionGuid: string,
) {
  return [
    ...inputs.employee.cashBonusTransactions,
    ...inputs.employee.restrictedStockUnitTransactions,
    ...inputs.employee.stockOptionTransactions,
    ...inputs.employee.restrictedStockTransactions,
  ].find((t) => t.transactionGuid === transactionGuid);
}

export function getTransactionInputs(
  calcContext: CalculationContext,
  snapshot: ActivatedRouteSnapshot,
) {
  const transactionGuid = getRouteParamFromRoot(snapshot, 'transactionGuid');
  const inputs =
    calcContext.calcEngineInputs != null
      ? findTransactionInputByGuid(
          calcContext.calcEngineInputs,
          transactionGuid,
        )
      : undefined;

  if (inputs == null) {
    throw new Error(`unable to find transaction with GUID: ${transactionGuid}`);
  }

  return inputs;
}

export function getTransactionResult(
  calcContext: CalculationContext,
  snapshot: ActivatedRouteSnapshot,
) {
  const transactionGuid = getRouteParamFromRoot(snapshot, 'transactionGuid');
  const results = calcContext.calcEngineResults?.transactionResults.find(
    (t) => t.transactionGuid === transactionGuid,
  );

  if (results == null) {
    throw new Error(`unable to find transaction with GUID: ${transactionGuid}`);
  }

  return results;
}

export class CalculationContext {
  constructor(
    calculationContextDto: ApiClient.CalculationContextDto,
    calculationContextApiService: ApiClient.CalculationContextService | null = null,
    getCalcEngineOriginalResults: () => Promise<ApiClient.ICalculationEngineResults | null> = () =>
      Promise.resolve(null),
    getLatestFormattedInputsAndResults: () => Promise<ApiClient.CalcEngineLatestFormattedInputsAndResults | null> = () =>
      Promise.resolve(null),
  ) {
    this.calculationId = calculationContextDto.calculationId;
    this.batchId = calculationContextDto.batchId;
    this.clientPortalId = calculationContextDto.clientPortalId;
    this.indexRecord = calculationContextDto.indexRecord;
    this.preProcessorInputs = calculationContextDto.preProcessorInputs;
    this.calcEngineInputs = calculationContextDto.calcEngineInputs;
    this.calcEngineResults = calculationContextDto.calcEngineResults;
    this.employerCalcEngineResults =
      calculationContextDto.employerCalcEngineResults;
    this.calcEngineFailureResult =
      calculationContextDto.calcEngineFailureResult;
    this.hasTeqConsideredWorkLocations =
      calculationContextDto.hasTeqConsideredWorkLocations;
    this.calculationCanBeClonedToOriginalBatch =
      calculationContextDto.calculationCanBeClonedToOriginalBatch;

    const defaultGetCalcEngineOriginalResults =
      calculationContextApiService == null
        ? () => Promise.resolve(null)
        : async () => {
            const originalResultsResponse = await calculationContextApiService
              .getCalculationEngineOriginalResults(
                this.clientPortalId,
                this.calculationId,
              )
              .toPromise();

            return originalResultsResponse.calculationEngineResults;
          };

    const defaultGetLatestFormmattedInputsAndResults =
      calculationContextApiService == null
        ? () => Promise.resolve(null)
        : async () => {
            try {
              const latestFormattedResponse = await calculationContextApiService
                ?.getLatestFormattedInputsAndResults(
                  this.clientPortalId,
                  this.calculationId,
                )
                .toPromise();

              return latestFormattedResponse.result;
            } catch (e) {
              console.error(e);
              return null;
            }
          };

    this.getCalcEngineOriginalResults =
      getCalcEngineOriginalResults ?? defaultGetCalcEngineOriginalResults;
    this.getLatestFormattedInputsAndResults =
      getLatestFormattedInputsAndResults ??
      defaultGetLatestFormmattedInputsAndResults;
  }

  calculationId!: string;
  batchId!: string;
  clientPortalId!: string;
  indexRecord!: ApiClient.CalculationIndexModel;
  preProcessorInputs!: ApiClient.PreProcessorInputs;
  calcEngineInputs!: ApiClient.CalculationEngineInputs | null;
  calcEngineResults!: ApiClient.EmployeeCalculationResults | null;
  employerCalcEngineResults!: ApiClient.EmployerCalculationResults | null;
  calcEngineFailureResult!: ApiClient.CalculateEmployeeCommandResultFailure | null;
  hasTeqConsideredWorkLocations!: boolean;
  calculationCanBeClonedToOriginalBatch!: boolean;

  getCalcEngineOriginalResults: () => Promise<ApiClient.ICalculationEngineResults | null>;
  getLatestFormattedInputsAndResults: () => Promise<ApiClient.CalcEngineLatestFormattedInputsAndResults | null>;
}
